import { ArrowLeft, ChevronRight, Shield } from "lucide-react";
import { useMemo } from "react";
import { Link, Navigate } from "react-router-dom";

import { PageContainer } from "@/components/PageContainer";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useAdminUtenti } from "@/hooks/useAdminUtenti";
import { useMe } from "@/hooks/useMe";
import { isMaitimeStaff } from "@/lib/nav-config";
import { cn } from "@/lib/utils";
import type { Ruolo } from "@/types/api";

interface RuoloInfo {
  ruolo: Ruolo;
  label: string;
  descrizione: string;
}

const RUOLI_INFO: RuoloInfo[] = [
  {
    ruolo: "maitime_staff",
    label: "Maitime Staff",
    descrizione: "Bypassa tutti i check (non configurabile).",
  },
  {
    ruolo: "mp_presidenza",
    label: "Presidenza",
    descrizione: "Direzione Mister Pet — Enzo, leadership.",
  },
  {
    ruolo: "mp_management",
    label: "Management",
    descrizione: "Direttori di funzione (commerciale, operativo, ...).",
  },
  {
    ruolo: "mp_planning",
    label: "Planning",
    descrizione: "Pianificazione dati — chi carica gli Excel (Giacomo).",
  },
  {
    ruolo: "mp_logistica",
    label: "Logistica",
    descrizione: "Logistica e spedizioni — Marina.",
  },
  {
    ruolo: "mp_commerciale",
    label: "Commerciale",
    descrizione: "Forza vendita — Fabio.",
  },
  {
    ruolo: "mp_viewer",
    label: "Viewer (sola lettura)",
    descrizione: "Profilo minimo, solo lettura.",
  },
];

export function AdminRuoliPage() {
  const { data: me, isLoading } = useMe();
  const { data: utenti = [] } = useAdminUtenti();

  const utentiPerRuolo = useMemo(() => {
    const map: Record<string, number> = {};
    for (const u of utenti) {
      if (!u.attivo) continue;
      map[u.ruolo] = (map[u.ruolo] ?? 0) + 1;
    }
    return map;
  }, [utenti]);

  if (!isLoading && me && !isMaitimeStaff(me)) {
    return <Navigate to="/" replace />;
  }

  return (
    <PageContainer>
      <Link
        to="/admin"
        className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors w-fit"
      >
        <ArrowLeft className="size-3.5" />
        Backoffice
      </Link>

      <header className="space-y-1">
        <p className="text-xs uppercase tracking-wider text-muted-foreground">
          Backoffice Maitime
        </p>
        <h1 className="text-3xl font-semibold tracking-tight text-foreground">
          Ruoli e permessi
        </h1>
        <p className="text-sm text-muted-foreground max-w-2xl pt-1">
          Configura il preset di permessi per ruolo. I permessi del singolo utente
          si modificano da <Link to="/admin/utenti" className="underline">Utenti e accessi</Link>.
        </p>
        <p className="text-xs text-muted-foreground max-w-2xl pt-1">
          <strong>Regola di backward-compatibility</strong>: se un ruolo non ha
          alcuna riga preset, applica il default legacy "tutto abilitato".
          Appena salvi la prima configurazione, il ruolo passa a regime
          esplicito: l'utente vedrà solo i permessi qui abilitati.
        </p>
      </header>

      <ul className="rounded-lg border bg-card divide-y">
        {RUOLI_INFO.map((r) => {
          const isStaff = r.ruolo === "maitime_staff";
          return (
            <li key={r.ruolo}>
              {isStaff ? (
                <div className="flex items-center gap-4 px-4 py-3.5 opacity-60 cursor-not-allowed">
                  <Shield className="size-4 text-muted-foreground shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-sm">{r.label}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {r.descrizione}
                    </p>
                  </div>
                  <Badge variant="outline" className="text-[10px]">
                    {utentiPerRuolo[r.ruolo] ?? 0} utenti
                  </Badge>
                  <Badge
                    variant="outline"
                    className="text-[10px] bg-muted text-muted-foreground"
                  >
                    non configurabile
                  </Badge>
                  <span className="size-4 shrink-0" />
                </div>
              ) : (
                <Link
                  to={`/admin/ruoli/${r.ruolo}/permessi`}
                  className={cn(
                    "flex items-center gap-4 px-4 py-3.5 hover:bg-muted/40 transition-colors group"
                  )}
                >
                  <Shield className="size-4 text-brand-700 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-sm">{r.label}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {r.descrizione}
                    </p>
                  </div>
                  <Badge variant="outline" className="text-[10px]">
                    {utentiPerRuolo[r.ruolo] ?? 0} utenti
                  </Badge>
                  <ChevronRight className="size-4 text-muted-foreground/50 group-hover:text-muted-foreground transition-colors shrink-0" />
                </Link>
              )}
            </li>
          );
        })}
      </ul>

      {isLoading && <Skeleton className="h-32 w-full" />}
    </PageContainer>
  );
}
